export type DocBadge = "Admin" | "Developer";

export type DocSnippetMessage = {
  role: "user" | "assistant";
  text: string;
};

export type DocItem = {
  id: string;
  title: string;
  badge?: DocBadge;
  snippet?: DocSnippetMessage[];
  content: string;
};

export type DocSection = {
  id: string;
  title: string;
  items: DocItem[];
};

export const DOC_SECTIONS: DocSection[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    items: [
      {
        id: "key-concepts",
        title: "Key Concepts",
        content: `# Key Concepts

Woods AI is a collaborative AI platform. Before diving in, here are the six ideas that underpin everything else.

## Teams

A team is a shared environment for collaboration. Everything — chats, agents, files, meetings, prompts — lives inside a team. You can belong to multiple teams and switch between them from the sidebar.

Your **Personal team** is created automatically when you sign up. It's private to you by default.

## Agents

An agent is a pre-configured AI assistant with a defined purpose. Instead of re-explaining your context in every chat, you create an agent once with a system prompt, a chosen model, and the tools it needs — then reuse it across conversations.

Think of agents as saved AI personas: a "Research Assistant", a "Meeting Summariser", or a "Civil Engineering Advisor".

## Tools

Tools extend what the AI can do mid-conversation:

| Tool | What it does |
|------|-------------|
| Web Search | Searches the web in real time |
| Web Fetch | Reads the content of a specific URL |
| Code Execution | Runs Python and processes files (PDF, DOCX, XLSX, PPTX) |
| Memory | Recalls facts you've stored about yourself |
| Custom Tools | Webhooks, internal APIs, and AWS tasks you configure |

Tools can be enabled per-agent or toggled per-session.

## Memory

Memory lets the AI remember things across conversations — your role, preferences, project context. You control what's stored, and you can delete memories at any time from the Memories panel.

Incognito mode disables memory entirely for a session.

## Personal profile

Your profile gives Woods AI optional background about you:

| Profile item | What it helps with |
|------|-------------|
| CV / Resume | Personalises answers using your role, experience, skills, education, and achievements |
| Writing Style Profile | Helps the AI write, rewrite, and edit in your own voice when enabled |

You can upload or update both from **Profile → General**.

## Layouts

Layouts are markdown templates that define the structure of a document — most commonly used for meeting minutes. Create a layout once and share it with your team; it's then available whenever minutes are generated from a meeting recording.

---
`,
      },
      {
        id: "quick-start",
        title: "Quick Start",
        content: `# Quick Start

Get from sign-in to a useful result in under five minutes.

## 1. Sign in

Open Woods AI and sign in with your organisational account. You'll land on the chat screen inside your Personal team.

## 2. Start a chat

Type your question or request in the input at the bottom of the screen and press **Enter**. The AI responds using the currently selected model (Sonnet by default).

You can switch models using the model selector next to the input.

## 3. Enable a tool

Click the **+** or tool icon in the chat input to add Web Search or other tools to the current session. The AI will use them automatically when relevant.

## 4. Use an agent

Click **Agents** in the sidebar to browse available agents. Selecting one starts a new chat pre-configured with that agent's prompt, model, and tools — no setup required.

## 5. Try a meeting

Click **Meetings** in the sidebar → **New Meeting**. Upload a recording or start transcribing. When you're done, generate minutes with one click.

---

That's the core loop. From here, explore:

- [Agents](#agents) — create your own AI configurations
- [Meetings & Transcription](#meetings) — the full meeting workflow
- [Memories](#memories) — make the AI remember your context
`,
      },
      {
        id: "first-chat",
        title: "Your First Chat",
        snippet: [
          {
            role: "user",
            text: "Summarise the key risks of building on expansive clay soils for a report section.",
          },
          {
            role: "assistant",
            text: "**Expansive Clay Soils — Key Risks**\n\nExpansive (shrink-swell) clays present several engineering risks that should be addressed in your report:\n\n- **Differential settlement** — Uneven moisture changes cause parts of the foundation to rise or sink at different rates, cracking slabs and walls.\n- **Heave during wet seasons** — Saturated clay swells, lifting lightly-loaded structures and floor slabs.\n- **Shrinkage cracking in dry periods** — Loss of moisture causes the soil to contract, opening voids beneath footings.\n- **Seasonal cycling** — Repeated wet/dry cycles accumulate permanent damage over time.\n\n**Mitigation measures** typically include deep footings below the active zone, moisture barriers, and site drainage design to maintain consistent soil moisture.",
          },
        ],
        content: `# Your First Chat

## Starting a conversation

The chat input is at the bottom of the screen. Type anything and press **Enter** or click the send button.

## Choosing a model

Three capability tiers are available:

| Tier | Best for |
|------|---------|
| **Standard** | Everyday tasks — fast, capable, balanced |
| **Advanced** | Complex reasoning, long documents, hard problems |
| **Lightweight** | Quick answers, simple lookups |

Switch models using the selector next to the chat input. The choice persists until you change it again.

## Temporary chat

Click the dashed-message icon (top right) to enter **Incognito mode**. The conversation isn't saved and memory is disabled. Useful for sensitive or exploratory questions.

## Attaching files

Drag a file onto the chat or use the attachment button. Supported: PDF, DOCX, XLSX, PPTX, images, and plain text. The Code Execution tool must be enabled to process office files.

## Chat history

Previous chats appear in the sidebar under your team. Click **Search** to find a specific conversation by keyword.
`,
      },
    ],
  },
  {
    id: "whats-new",
    title: "What's New",
    items: [
      {
        id: "whats-new",
        title: "What's New",
        content: `# What's New

The latest changes to Woods AI, newest first.

## October 2026

### Ask the Documentation
The Documentation page is now a chat. Ask how to do something in Woods AI and get an answer drawn from these guides, with links to the pages it used. Try **What's new?**, **Where do I start?** or **Make me an AI expert**. Click **Browse docs** to read the guides yourself. These chats aren't saved.

### Skills with /
Type **/** in the chat box to pick a skill, such as **/woods-docx** for Woods-branded Word documents or **/xlsx** for spreadsheet analysis. Skills switch on Code execution automatically for that request. See **Working with Files → Creating & Editing**.

### Working with Files guides
New guides cover chat attachments and their limits, when to use the Library instead, and the "write in markdown, then convert to Word" approach.

### Layouts for everyone
Anyone can now create a meeting minutes layout. Layouts are private until you share them with people or whole teams. Find them under **Meetings → Create new layout**.

### Word templates kept intact
When you attach a Word document and ask for changes, the original file is edited directly, so your logo, headers, footers and styles stay as they were.

### Projects
- **Pinned projects** now appear in the sidebar
- Projects have an **activity log**, and project settings can only be changed by the owner
- Project notes are now called **Registers**
- **Artifacts** can be edited and published from a project, and can read and update the project's registers

### Library refresh
The Library (formerly Knowledge Base) has a new design with file previews. Open a collection and click **Start chat**, or select files and start a chat, to ask questions answered only from those documents.

### Feedback
You can now send general feedback about Woods AI, not just thumbs up or down on a single answer.

## September 2026

### My files
Turn on **My files** in your profile so the AI can search your own Library files from any chat, without needing an agent.

### Ask Woods AI to do things for you
In chat you can ask Woods AI to list or create agents, find meetings, search across your projects and past chats, or move a chat into a project. Anything that changes something asks for your approval first.

### Responses that keep going
If you refresh the page or lose your connection, a response that was being written carries on and is waiting when you come back.

### Long messages
Long messages you send are collapsed behind **Show more** to keep chats easy to read.
`,
      },
    ],
  },
  {
    id: "personalisation",
    title: "Personalisation",
    items: [
      {
        id: "cv-upload",
        title: "CV / Resume Upload",
        content: `# CV / Resume Upload

Uploading your CV gives Woods AI a structured understanding of your professional background. It is used to personalise answers, remember relevant career context, and reduce how often you need to explain your role or experience.

## Where to upload

You can upload a CV in two places:

- During onboarding, after the name steps
- From **Profile → General → CV / Resume**

You can skip the onboarding upload and add it later from your profile.

## Supported files

Supported formats:

| Format | Notes |
|------|------|
| PDF | Parsed directly by the AI model |
| DOCX / DOC | Text is extracted before analysis |
| TXT | Read as plain text |

Maximum file size: **10 MB**.

## What gets extracted

The CV upload function analyses the file in categories:

- Personal details
- Professional summary
- Work experience
- Skills and tools
- Education
- Projects and portfolio
- Achievements, awards, certifications, and publications

Only fields found in the CV are saved.

## What gets stored

When processing completes:

- A structured copy is saved to your profile bio
- Resume-derived memories are saved under \`/memories/resume\`
- Older resume memories are replaced so re-uploads do not create duplicates
- Onboarding is marked complete when the upload happens during onboarding

These memories help Woods AI answer with awareness of your background. You can review and delete memories from the Memories panel.

## Replacing your CV

Open **Profile → General → CV / Resume** and upload a new file. The new upload refreshes your profile bio and replaces the previous resume memory entries.
`,
      },
      {
        id: "writing-style-profile",
        title: "Writing Style Profile",
        content: `# Writing Style Profile

The Writing Style Profile captures how you write so Woods AI can draft, rewrite, and edit text in your own voice when you ask it to.

## Where to update it

Open **Profile → General → Writing Style** and click **Update writing style**. You can upload multiple examples at once.

The same screen also includes a rendition tool: paste a text sample, choose a style suggestion, and Woods AI rewrites the text with Claude Haiku while preserving the original meaning.

Supported formats:

| Format | Notes |
|------|------|
| PDF | Passed to the model for analysis |
| DOCX / DOC | Text is extracted before analysis |
| TXT | Read as plain text |

Maximum file size: **10 MB per file**.

## What the analysis does

The writing-style function runs several analyses over your samples:

- Tone and voice
- Sentence craft
- Vocabulary and expression
- Structure and argument style

It then synthesises a reusable style guide and saves it to \`profiles.writing_style\`.

## How chats use it

The saved style profile is available through the **Writing style profile** tool.

To use it:

1. Open **Profile → AI & Tools**
2. Turn on **Writing style profile**
3. Ask Woods AI to write, rewrite, or edit something in your style

The AI loads the saved profile only when the tool is enabled and the task calls for it.

## Trying style renditions

Use the suggestions on the writing-style screen to compare different rewrites of the same text:

- My saved style
- Warm + concise
- Direct + polished
- Technical + precise
- Executive + brief

This is useful for testing your saved profile or quickly converting a draft into a different tone.

## Reviewing or editing it

Open **Profile → General → Writing Style** and click the info button next to the saved status. You can switch to markdown mode, edit the profile directly, and save changes.

## Good writing samples

Use examples that represent how you want future writing to sound. A few stronger samples are better than many weak ones.

Good samples include:

- Reports or memos you wrote yourself
- Emails that reflect your normal professional voice
- Proposals, advice notes, or project updates

Avoid samples that were heavily edited by someone else unless that edited style is what you want Woods AI to match.
`,
      },
    ],
  },
  {
    id: "core-features",
    title: "Core Features",
    items: [
      {
        id: "agents",
        title: "Agents",
        content: `# Agents

An agent is a reusable AI configuration. When you start a chat with an agent, it brings its own system prompt, model, tools, and context settings — so you don't have to repeat yourself.

## Creating an agent

1. Click **Agents** in the sidebar
2. Click **Create new agent**
3. Fill in the details (see below)
4. Click **Save**

## Agent settings

### Basic
- **Name** — displayed in chats and the agent list
- **Type** — *Knowledge* (focused on a domain) or *Custom* (general purpose)
- **Description** — a short summary of what the agent does
- **Model** — the default model for chats using this agent

### Instructions
The instructions define the agent's persona, expertise, and behaviour. They're injected at the start of every conversation. Use them to:

- Establish role and tone ("You are a senior civil engineer…")
- Set output format expectations
- Provide standing context (project names, terminology)

### Tools
Toggle which tools the agent can use by default:

- Web search
- Web fetch
- Code execution
- Memory

### Context
Control what background context is included automatically:

- **Your profile** — includes your name, role, and bio
- **Team context** — includes team-level instructions
- **Woods context** — Woods-specific background
- **NZ context** — New Zealand regulatory and industry context
- **Civil engineering context** — domain knowledge for engineering work

### Team assignment
Assign the agent to one or more teams so the right team members can access it.

## Using an agent

Click **Agents** in the sidebar, select an agent, and click **Chat**. A new conversation opens pre-loaded with the agent's configuration.

You can override the model or toggle tools within a specific conversation without affecting the agent's default settings.
`,
      },
      {
        id: "meetings",
        title: "Meetings & Transcription",
        content: `# Meetings & Transcription

The Meetings feature handles the full lifecycle of a meeting: recording → transcription → minutes → distribution.

## Creating a meeting

1. Click **Meetings** in the sidebar
2. Click **New Meeting** — a meeting record is created with today's timestamp as the title
3. You're taken to the meeting page

## Uploading a recording

On the meeting page, upload an audio or video file. The file is transcribed automatically in chunks. Large recordings are processed in ~30-second segments.

**Speaker identification** is applied automatically. Speakers are labelled Speaker A, Speaker B, etc. You can rename them in the editor.

## Meeting context

Before generating minutes, add context in the **Meeting context** field:
- What the meeting was about
- Key decisions already known
- Any acronyms or project names the AI should know

This significantly improves the quality of generated minutes.

## Generating minutes

Click **Generate minutes**. The AI produces structured minutes using the selected layout template. Minutes include:

- Attendees (with internal/external classification)
- Summary
- Discussion points
- Action items with owners and due dates
- Decisions made

If a **previous meeting** is linked, outstanding action items from that meeting are carried forward automatically.

## Recording controls

The recording toolbar appears at the top of the meeting page once you open or create a meeting.

\`\`\`mermaid
flowchart LR
  A([▶ Play / Record]) -->|recording starts| B([🔇 Mute / 🔊 Unmute])
  B -->|toggle mic| B
  B -->|click Stop ⏹| C([⏹ Stop])
  C --> D([📝 Meeting Notes generated])
  D --> E{Review & edit notes}
  E -->|make changes| E
  E --> F([📋 Copy])
  E --> G([⬇ Download — Woods template])
\`\`\`

| Control | What it does |
|---------|-------------|
| **▶ Play / Record** | Starts the recording and transcription session |
| **🔇 / 🔊 Mute · Unmute** | Toggles your microphone while recording continues |
| **⏹ Stop** | Ends the session and triggers automatic note generation |
| **Edit** | Click into the notes area to make inline changes |
| **📋 Copy** | Copies the formatted notes to your clipboard |
| **⬇ Download** | Downloads a PDF in the Woods template |

## Layouts

Minutes follow a **layout template**. Select a layout from the dropdown on the meeting page. You can create your own layouts from **Meetings → Create new layout** and share them with your team.

## Exporting and sharing

- **PDF export** — download a formatted PDF of the minutes
- **Email** — send the minutes directly from the meeting page

## Editing minutes

Minutes are editable markdown. Click into the minutes area to make changes before exporting.
`,
      },
      {
        id: "knowledge-base",
        title: "Library",
        content: `# Library

The Library is a file library for your team. Upload documents once and attach them to agents — so the AI can reference your files in every conversation without you needing to re-attach them each time.

> **Files only.** The Library stores documents. Chat history and conversations are not stored here.

## How files are organised

Files sit in a three-level hierarchy:

| Level | What it is |
|-------|-----------|
| **Team** | A top-level container for a team's files |
| **Collection** | A folder inside a team for grouping related documents |
| **File** | An individual document |


## Uploading files

1. Click **Library** in the sidebar
2. Select the team and collection you want to upload into
3. Click **Upload** or drag and drop files onto the panel

Supported formats: PDF, DOCX, XLSX, PPTX, images, and plain text.

## Using files in your agents

Files in the Library can be attached to any agent you own. When a chat starts with that agent, the relevant content is retrieved automatically — no need to attach the file in each conversation.

To attach files to an agent:

1. Click **Agents** in the sidebar and open the agent editor
2. Go to the **Tools** section and enable **Knowledge Base**
3. Select the files or collections the agent should reference

## Requesting to move files

If a file belongs in a different team, you can request a transfer rather than re-uploading it.

1. Select the file in the Library panel
2. Click **Request move** and choose the target team
3. The team owner approves or declines the request

Admins can view all pending requests under **File Move Requests** in the menu.
`,
      },
      {
        id: "prompt-library",
        title: "Prompt Library",
        content: `# Prompt Library

The Prompt Library saves prompts you use repeatedly, so you can insert them into any conversation with one click.

## Creating a prompt

1. Click **Prompts** in the sidebar (or the prompt icon)
2. Click **Create new prompt**
3. Give it a name and write the prompt content
4. Choose whether to keep it private or share it with your team
5. Save

## Using a saved prompt

Click **Prompts** in the sidebar → select a prompt → **Use**. The prompt is inserted into the current chat input, ready to send or edit.

## Organising prompts

Prompts can be grouped into folders. Create a folder for each project or category to keep the library tidy.

## Sharing

A prompt marked as **shared** is visible to all members of the team. Private prompts are only visible to you.

## Tips for effective prompts

- Be specific about the output format you want
- Include examples where helpful ("Format the response as a bullet list like this: …")
- Use placeholders for variable content ("[project name]", "[date]") and fill them in before sending
`,
      },
      {
        id: "memories",
        title: "Memories",
        content: `# Memories

Memory lets Woods AI remember facts about you across conversations — so you don't have to re-introduce yourself every session.

## How memory works

When you chat, the AI can automatically extract and store relevant facts: your role, your current projects, your preferences. These are recalled at the start of future conversations and injected into the system context.

## Viewing your memories

Click **Memories** in the sidebar to see everything stored. Each memory shows its content, when it was created, and the source (chat, project, or profile).

## Adding a memory manually

In the Memories panel, click **Add memory** and type the fact you want the AI to remember. Examples:

- "I'm a project engineer at Northland Council"
- "Always format code examples in Python"
- "My current project is the Mangakahia Bridge rehabilitation"

## Deleting a memory

Click the delete icon next to any memory to remove it. The fact will no longer be included in future conversations.

## Incognito mode

Enable **Incognito mode** (the dashed message icon, top right of the chat screen) to have a conversation where memory is neither read nor written. Useful for sensitive topics or one-off questions.

## Memory tags

Memories are tagged by source type (chat, manual, profile, project) to help you understand where a memory came from.
`,
      },
    ],
  },
  {
    id: "working-with-files",
    title: "Working with Files",
    items: [
      {
        id: "files-uploads",
        title: "Uploads",
        content: `# Uploads

There are two ways to give Woods AI your documents. Pick the one that matches how long you need the file.

| | Chat attachment | Files (Library) |
|---|---|---|
| **Best for** | A one-off question about a file | Documents you'll come back to again and again |
| **Lives in** | That one chat only | Your file library, ready for any chat |
| **How the AI reads it** | Reads the whole file | Searches for the relevant passages |
| **Size** | PDFs up to 32 MB | Handles large and long documents |

## Chat attachments

A chat attachment is a file you add straight into a conversation. The AI reads it as part of your message, and it stays with that chat. If you come back to the chat later, the file is still there. It is not added to your Files library and is not available in other chats.

### How to attach

- Click the **Add attachment** button in the chat box and choose **Add photos or files**
- Drag files onto the chat box
- Paste an image or file from your clipboard
- Choose **Take screenshot** to capture your screen

You can attach several files at once. If you paste a very long block of text (over 2,000 characters), it's turned into a text file attachment so your message stays tidy.

### What you can attach

| File type | What happens |
|---|---|
| **PDF, images** (JPG, PNG, GIF, WebP) | The AI reads them directly, including tables and pictures |
| **Excel and CSV** (.xlsx, .xls, .csv) | Loaded into a workspace where the AI can run calculations on the real data |
| **Word** (.docx) | Read directly, and the original file is kept so the AI can edit it without losing your formatting |
| **PowerPoint** (.pptx) | The text is read |
| **Web pages** (.html, .htm) | You're asked how to read it. **As code** puts the original file in the code execution workspace (Code execution is turned on for the chat) so the AI can inspect or edit the markup. **As a rendered page** converts it to a PDF in your browser so the AI reads it as it looks. The first conversion downloads a one-off converter, which can take a minute |
| **Text and code** (.txt, .md, .json, .py, .sql …) | Read directly |

### Limits

- **PDFs are limited to 32 MB.** If yours is bigger, split or compress it, or upload it to **Files** instead, which handles large PDFs.
- **Very long text is cut short.** If a document has more than about 500,000 characters of text, only the first part is read. Turn on **Code execution** so the whole file can be loaded instead.
- **Spreadsheets and Word files need Code execution** to be worked on as real files. With it turned off, the AI only sees their text. Code execution is on by default and can be changed in **Profile → Code execution**.

## Analysing spreadsheets with /xlsx

For Excel or CSV files, type **/xlsx** at the start of your message. This loads the spreadsheet skill, which gives the AI proper tools for reading, calculating and charting your data. Code execution is switched on automatically for that request.

\`\`\`text
/xlsx Here is our timesheet export for September. Total the hours
by project, flag anyone over 45 hours in a week, and give me a
summary table I can paste into a report.
\`\`\`

Good things to ask for:

- Totals, averages and breakdowns by column
- Finding duplicates, gaps or unusual values
- Charts of trends over time
- A cleaned-up or reformatted spreadsheet to download

> **Tip:** Type **/** in the chat box to see every available skill. Hover over a name to see what it does.

## Files (Library)

**Files** is your personal document library, also known as the Knowledge Base. Upload a document once and it's ready whenever you need it, with no re-attaching in every chat.

When you upload, each document is read and indexed so the AI can search it quickly. This means you can keep large documents here, like standards, specifications, reports and manuals, and the AI will pull out just the parts that answer your question.

### When to use Files instead of an attachment

Upload to **Files** when:

- You'll ask about the document more than once
- The file is too big to attach (for example, a 300-page PDF)
- You want to ask questions across several related documents at once
- You want answers that point to the exact file the information came from

Use a **chat attachment** when it's a quick, one-off question about a single file.

### Uploading

1. Open **Library** from the sidebar
2. Optionally open a **collection** (a folder for related documents)
3. Click **Upload**, or drag and drop files onto the page

Each file shows **Processing** while it's being read, then **Ready** once it can be searched. Large documents can take a few minutes. If a file shows **Failed**, use **Retry**.

### Chatting with your files

There are two ways to talk to documents you've uploaded.

**From the Files page (recommended).** This is the quickest way to ask about specific documents.

1. Open a collection and click **Start chat**, or use **Start chat** from the collection's menu
2. Or tick one or more files and click the chat button in the selection bar

A new chat opens with a badge showing which files it's linked to. The AI answers **only** from those documents and names the file each point came from. If the answer isn't in your files, it will tell you rather than guess.

**From any chat.** Turn on **My files** in your profile settings. The AI can then search all your uploaded files whenever a question needs them, without you having to choose them first.

> Files can also be attached to an **Agent**, so the agent always has your reference documents to hand. See **Core Features → Library** for details.
`,
      },
      {
        id: "files-creating",
        title: "Creating & Editing",
        snippet: [
          {
            role: "user",
            text: "Write a short site visit summary for 14 Rata Street as a markdown file. Include attendees, observations and next steps.",
          },
          {
            role: "assistant",
            text: "# Site Visit Summary – 14 Rata Street\n\n**Date:** 10 October 2026\n\n## Attendees\n- J. Smith (Woods)\n- A. Brown (Client)\n\n## Observations\n- Overland flow path blocked by fencing at the rear boundary\n- Existing sump in good condition\n\n## Next steps\n- Confirm fence relocation with the client\n- Update stormwater design by 24 October\n\nWhen you're happy with this, send **/woods-docx** to turn it into a Woods-branded Word document.",
          },
        ],
        content: `# Creating & Editing

Woods AI can create new documents for you and edit documents you already have. The best results come from a simple two-step approach: **write the content first, then format it.**

## Step 1 – Start with a markdown file

### What is a markdown file?

Markdown (a **.md** file) is plain text with a few simple symbols that mark the structure:

| You type | You get |
|---|---|
| \`# Title\` | A main heading |
| \`## Section\` | A sub-heading |
| \`**important**\` | **Bold text** |
| \`- item\` | A bullet point |
| \`1. step\` | A numbered list |

That's all there is to it. It's easy to read as-is, and the AI can write and change it quickly.

### Why start with markdown?

- **It's fast to change.** You can go back and forth on wording, order and content without waiting for a Word document to be rebuilt each time.
- **You focus on the content.** Get the words right first, and leave fonts, logos and layout until the end.
- **It converts cleanly.** Headings, lists and tables in markdown become proper Word headings, lists and tables.

### Example

Ask for a draft in markdown:

\`\`\`text
Draft a memo to the project team about the change to the
stormwater design at 14 Rata Street. Write it as a markdown file.
Keep it under one page.
\`\`\`

The AI creates a file like this, which you can preview and keep refining:

\`\`\`markdown
# Stormwater Design Change – 14 Rata Street

## Summary
The attenuation tank has been moved to the north-east corner
to avoid the existing water main.

## What this means
- Pipe lengths increase by about 12 m
- No change to the consent conditions
- Revised drawings due 24 October

## Actions
1. Civil team to update the drainage layout
2. Project manager to notify the client
\`\`\`

Then refine it in plain language, for example *"make the summary shorter"*, *"add a section on cost impact"* or *"turn the actions into a table with owners and dates"*.

## Step 2 – Convert to a Woods Word document with /woods-docx

When the content is right, type **/woods-docx** and say which kind of document you want:

\`\`\`text
/woods-docx Turn this into a Woods memo addressed to the project team.
\`\`\`

The AI converts your markdown into a Woods-branded Word file with the correct logo, fonts and layout, ready to download.

**/woods-docx** supports these Woods templates:

- Report
- Memorandum
- Offer of Service (short or long)
- Meeting Minutes (general or PUD)
- Letterhead (short or long)
- Blank branded document

> **Tip:** Include the details the template needs, like the recipient, project number, date and author, so every field is filled in.

## Editing an existing document

To change a document you already have, attach it to the chat and say what you want changed:

\`\`\`text
/docx Update the dates in section 3 to October 2026 and fix
any spelling mistakes. Keep everything else the same.
\`\`\`

When you attach a Word file, the original is kept and edited directly, so your logo, headers, footers and styles stay exactly as they were. The AI won't switch your document to Woods branding unless you ask it to.

## Other file skills

Type **/** in the chat box to see all skills. The ones for files are:

| Skill | Use it to |
|---|---|
| **/woods-docx** | Create Woods-branded Word documents |
| **/woods-meeting-notes-docx** | Turn meeting notes into a Woods Word document |
| **/docx** | Read and edit any Word document |
| **/xlsx** | Read, analyse and build Excel spreadsheets |
| **/pptx** | Read and build PowerPoint decks |
| **/pdf** | Read, fill in and build PDF files |

Skills switch on **Code execution** automatically for that request, so they work even if you've turned it off.
`,
      },
    ],
  },
  {
    id: "projects",
    title: "Projects",
    items: [
      {
        id: "projects",
        title: "Projects",
        content: `# Projects

A project is a focused workspace where you bring together the people, files, meetings, and conversations for a specific piece of work.

## Creating a project

1. Click **Projects** in the sidebar
2. Click **New Project**
3. Enter a name (required) and an optional description
4. Click **Create project**

Projects are private by default — only invited members can see them.

## The project workspace

The project page is split into two areas:

**Left — Conversations**

- A chat input at the top to start a new conversation in this project's context
- **Project chats** — conversations shared with all project members
- **Your private chats** — conversations only visible to you

Every chat started from a project runs with the project's instructions and files automatically applied — no manual setup needed.

> **Note:** Incognito chats are not saved and cannot be added to a project.

**Right — Context panel**

| Panel | What it shows | Who sees it |
|-------|---------------|-------------|
| Memory | Facts the AI has remembered from your project chats | You only |
| Instructions | Standing context injected into every project chat | All members |
| Agents | Agents attached to the project | All members |
| Artifacts | Artifacts attached to the project | All members |
| Files | Files from your library attached to this project | All members |
| Meeting minutes | Recordings attached to this project | All members (editors see all; viewers see only recordings they attended) |

## Deleting a project

Open the project → click the **⋯** menu in the header → **Delete project**. Only the project owner can delete a project.
`,
      },
      {
        id: "project-context",
        title: "Instructions & Files",
        snippet: [
          {
            role: "user",
            text: "Draft a brief project update email to the client.",
          },
          {
            role: "assistant",
            text: "Here's a draft based on the project context:\n\n---\n\n**Subject: Mangakahia Bridge Rehabilitation — Stage 3 Update**\n\nHi [Client name],\n\nI wanted to provide a brief update on the Stage 3 earthworks design.\n\nWe have completed the geotechnical review and confirmed bearing capacity at both abutment locations. The earthworks ITP has been revised and is with you for sign-off. We are targeting pile design commencement once the final load schedule is confirmed.\n\nPlease let us know if you have any questions.\n\nKind regards,\n[Your name]\n\n---\n\nNote: I've used NZD and referenced NZ building code as set in the project instructions. Let me know if you'd like a different tone or format.",
          },
        ],
        content: `# Instructions & Files

## Instructions

Instructions are standing context injected into the system prompt of every chat in this project. Use them to give the AI persistent background it should always know about the project.

Good uses for project instructions:

- Project name, client, location, and scope
- Key terminology or abbreviations used in this project
- Output format preferences ("Always summarise action items in a table")
- Constraints ("All costs are in NZD. Reference NZ building code only.")

**To edit instructions:**

1. Click the pencil icon in the **Instructions** panel (right side of the project page)
2. Write in markdown — use the **Preview** toggle to check formatting
3. Click **Save**

Instructions are visible to all project members. Only members with editor access can change them.

---

## Files

Attach files from your Library to make them available in every project chat automatically.

**To attach a file:**

1. Click **+** in the **Files** panel
2. Search your file library and select the file
3. Click **Attach**

The file is now scoped to this project — the AI will automatically search and reference it in any project chat without you having to attach it each time.

**To remove a file:**

Click the **×** on an attached file. This detaches it from the project but does not delete it from your library.

Files are visible to all project members. Only editors can attach or remove files.

---

## Meeting minutes

Attach meeting recordings that are relevant to this project.

**To attach a recording:**

1. Click **+** in the **Meeting minutes** panel
2. Select a recording from your meetings list
3. Click **Attach**

**Access:** Editors and the project owner can see all attached recordings. Members with view-only access can only see recordings they attended.

To detach a recording, hover over it and click the remove icon.

---

## Memory

The **Memory** panel on the right side of the project page shows facts the AI has remembered from your conversations in this project. These are scoped to you — other members see their own project memories, not yours.

Project memories are created automatically as you chat. They capture things like decisions made, client preferences, or context specific to this piece of work — so the AI carries that knowledge into future project conversations without you having to repeat it.

`,
      },
      {
        id: "project-agents-artifacts",
        title: "Agents & Artifacts",
        content: `# Agents & Artifacts

Bring the agents and artifacts your project relies on into one place, so every member can find and use them without hunting through their own library.

Both panels sit on the right side of the project page and are visible to **all project members**. Only the owner and editors can attach or remove items.

---

## Agents

The **Agents** panel lists the agents attached to this project. Attach an agent so the whole team knows which AI configuration to use for this piece of work — then start a project chat with it in one click.

**To attach an agent:**

1. Click **+** in the **Agents** panel
2. Search your agents and select one
3. It appears as a card for project members who have access to that agent (see the note below)

**To start a chat with a project agent:**

1. Click an agent card — it highlights to show it's selected, and its name appears as a chip above the chat input
2. Type your message and send — the new project chat opens pre-loaded with that agent's prompt, model, and tools
3. Click the card again (or the chip) to clear the selection and chat without an agent

**To remove an agent:** hover over its card and click the remove icon. This detaches it from the project; it stays in your agent library.

> **Good to know:** attaching an agent lists it for the team, but a member only sees its card — and can only use it — if they already have access to that agent (their own, a shared/public agent, or one shared with a team they belong to). Members without access won't see the card at all. To make an agent visible and usable for everyone on the project, share it with a team all members belong to, or set its sharing so it isn't private.

---

## Artifacts

The **Artifacts** panel lists the artifacts attached to this project — interactive HTML tools, calculators, dashboards, and the like. Attaching an artifact makes it viewable by **every project member, even if the artifact is otherwise private**.

**To attach an artifact:**

1. Click **+** in the **Artifacts** panel
2. Search your artifacts and select one
3. It appears as a card for every project member

**To open an artifact:** click its card to view it on the artifact page.

**To remove an artifact:** hover over its card and click the remove icon. This detaches it from the project (and removes the project-wide view access) but does not delete the artifact from your library.

> **Tip:** artifacts are a great way to share a reusable calculator or dashboard with everyone working on the project without changing the artifact's own sharing settings.
`,
      },
      {
        id: "project-members",
        title: "Members & Sharing",
        content: `# Members & Sharing

## Adding members

1. Click **Share** in the project header
2. Search by name or username
3. Select the person and choose their role
4. Click **Add**

You can only share projects with specific individuals. Organisation-wide sharing must be enabled by an admin.

## Member roles

| Role | What they can do |
|------|-----------------|
| **Owner** | Full access — edit everything, manage members, delete the project, and revoke any shared chat |
| **Editor** | Edit instructions, attach and remove files and meeting recordings, invite members, change roles, toggle chat sharing |
| **Viewer** | Read-only — see project chats, files, instructions, and meeting recordings they attended |

The project owner is always the person who created the project.

**To change a member's role:** Open **Share** → click the role dropdown next to their name.

**To remove a member:** Open **Share** → click the remove option next to their name.

---

## Chat sharing within a project

Chats started inside a project can be private or shared:

| Type | Icon | Who can see it |
|------|------|----------------|
| **Private** | Lock | Only you |
| **Shared with project** | Users | All project members |

New chats start as private. Share a chat when the conversation is useful for the whole team.

**To toggle:** Hover over a chat in the list → click the lock or users icon on the right.

**Who can toggle:**
- The chat owner (if they have editor access)
- The project owner (can also make any shared chat private, even ones they didn't create)
`,
      },
    ],
  },
  {
    id: "tools",
    title: "Tools & Integrations",
    items: [
      {
        id: "builtin-tools",
        title: "Built-in Tools",
        content: `# Built-in Tools

Built-in tools extend what the AI can do within a conversation. They're available to all users and can be toggled per-session or set as defaults on an agent.

## Web Search

Searches the web in real time and returns relevant results. The AI cites its sources.

**When to use:** Current events, pricing, recent publications, anything that may have changed since the model's training cutoff.

**Limit:** Up to 10 searches per conversation.

**How to enable:** Click the tool icon in the chat input and toggle **Web search**.

## Web Fetch

Fetches and reads the full content of a specific URL. Useful when you have a link and want the AI to analyse or summarise it.

**How to use:** Enable the tool, then paste a URL into your message: "Summarise the content at https://example.com/report.pdf"

## Code Execution

Runs Python code in a secure container. Also processes uploaded office files (PDF, DOCX, XLSX, PPTX) — for example, extracting tables from a spreadsheet or summarising a Word document.

**How to enable:** Toggle **Code execution** in the chat input tools.

**What it can do:**
- Run calculations
- Parse and transform data files
- Generate charts (returned as images)
- Process uploaded documents

## Memory

When enabled, the AI reads your stored memories at the start of a conversation and can write new ones during it. See [Memories](#memories) for full details.

`,
      },
      {
        id: "custom-tools",
        title: "Custom Tools",
        badge: "Admin" as DocBadge,
        content: `# Custom Tools

Custom tools let you connect the AI to your own systems — internal APIs, databases, webhooks, and AWS tasks.

> **Admin access required** to create and manage custom tools.

## Tool types

### HTTP / Webhook
Call any HTTP endpoint. Configure the URL, method, headers, and authentication. The AI passes parameters defined in a JSON schema.

**Use cases:** Post to Slack, trigger a Jenkins build, look up a record in your CRM.

### AWS ECS
Run a containerised task on AWS ECS. Useful for longer-running jobs that need compute — data processing, report generation, document conversion.

### MCP (Model Context Protocol)
Connect to an MCP server to expose a structured set of tools and resources. MCP is the emerging standard for AI tool interoperability.

### Internal / Edge Function
Call a Supabase edge function or internal backend endpoint. Useful for tools that need direct database access or Woods-specific logic.

## Creating a custom tool

1. Navigate to **More → Tools** (Admin)
2. Click **Create new tool**
3. Configure:
   - **Name** and description (the AI reads these to decide when to use the tool)
   - **Executor type** (HTTP, ECS, MCP, etc.)
   - **Parameters** — defined as a JSON Schema. Each parameter the AI can pass must be declared here.
   - **Authentication** — API keys, Bearer tokens, or AWS credentials
4. Assign the tool to a team

## Parameter schema example

\`\`\`json
{
  "type": "object",
  "properties": {
    "project_id": {
      "type": "string",
      "description": "The ID of the project to look up"
    },
    "include_archived": {
      "type": "boolean",
      "description": "Whether to include archived records"
    }
  },
  "required": ["project_id"]
}
\`\`\`

## Assigning tools to agents

In the agent editor, go to **Tools** and add your custom tool. It will then be available in every conversation using that agent.
`,
      },
    ],
  },
  {
    id: "layouts",
    title: "Layouts & Templates",
    items: [
      {
        id: "layouts-overview",
        title: "Creating Layouts",
        content: `# Layouts & Templates

Layouts define the structure of generated documents — most commonly meeting minutes. Anyone can create a layout. It's private to you until you share it with people or teams.

## What a layout defines

A layout is a markdown template that tells the AI what sections to produce and in what order. The AI fills in each section based on the meeting transcript.

## Layout types

| Type | Use case |
|------|---------|
| **Meeting minutes** | Structured notes from a recorded meeting |
| **Report** | Formal written reports with executive summary |
| **General** | Flexible template for any document type |

## Creating a layout

1. Open **Meetings**, click the arrow next to **New Meeting**, and choose **Create new layout**
2. Fill in:
   - **Name** — shown in the layout picker on the meeting page
   - **Description** — what the layout is for
   - **Type** — Meeting Minutes, Report, or General
   - **Structure** — the markdown template (see below)
3. Save, and make sure **Active** is on so it appears in the picker

To see or edit your layouts later, choose **Manage layouts** from the same menu.

## Writing a structure template

The structure is a markdown document. Use headings to define sections. Add instructions in comments or angle brackets to guide the AI.

**Example — Engineering Construction minutes:**

\`\`\`markdown
# Meeting Minutes

## Meeting Details
- **Date:** [date]
- **Location:** [location]
- **Attendees:** [list all attendees with organisation]

## Apologies
[List any apologies received]

## Previous Action Items
[Carry over outstanding items from the previous meeting]

## Agenda Items

### [Item 1 title]
**Discussion:**
[Summary of discussion]

**Decision:**
[Any decisions made]

**Actions:**
- [ ] [Action] — [Owner] — [Due date]

## Next Meeting
- **Date:** [proposed date]
- **Location:** [proposed location]
\`\`\`

## Sharing a layout

New layouts are private to you. To let others use them, open the layout and click **Share**:

- **Add people** — search by name, username, or email
- **Shared teams** — tick a team and everyone on it gets access

Anyone you share with can use, edit, and re-share the layout. You can remove access at any time.

The built-in layouts (**General** and **Engineering Construction**) are available to everyone. Only admins can edit them.

## Activating and deactivating

Toggle the **Active** switch on a layout to control whether it appears in the meeting minutes layout picker. Inactive layouts are hidden from users but not deleted.

## Configuration (advanced)

The **Config** field accepts a JSON object for advanced layout options. Currently used for AI generation parameters (temperature, instructions). Leave blank to use defaults.
`,
      },
    ],
  },
  {
    id: "artifacts",
    title: "Artifacts",
    items: [
      {
        id: "artifacts-generator",
        title: "Artifacts",
        content: `# Artifacts

Artifacts are self-contained interactive HTML pages — calculators, dashboards, maps, and tools — built by AI from a plain-language description and stored in your account.

## Creating

**From the generator** — Go to **Artifacts → Generate**, pick a suggestion or write a prompt, click Generate. Preview appears instantly. Click **Save as Artifact** to keep it.

**From a chat** — When the AI produces an HTML page in chat, a Save button appears inline. Name it and it's saved.

**Upload** — Drag any existing \`.html\` file onto the Artifacts panel.

## Generator suggestions

Pick a category and click a suggestion to load a ready-made prompt — or write your own.

| Category | Examples |
|----------|---------|
| Civil Engineering | Stormwater pipe sizer, retaining wall stability |
| Water Infrastructure | Hazen-Williams head loss, Rational Method Q |
| Surveying | Traverse closure with Bowditch, area by coordinates |
| Geospatial | NZTM2000 ↔ WGS84 converter, NZ region map |
| Earthworks | Cut/fill end area volumes, batter slope setback |
| Planning & Urban Design | Subdivision lot yield, site coverage calculator |
| Land Development | Infrastructure cost apportionment, flood freeboard check |

## Managing

Open any artifact to see a live **Preview** or edit its name, description, and sharing in **Details**.

**Sharing levels:** Private → Team → Public (anyone with the link).

**Cross-team:** Submit a share request from Details. The target team's admin approves or rejects it.

**Delete:** Details tab → Delete. Permanent.
`,
      },
    ],
  },
  {
    id: "administration",
    title: "Administration",
    items: [
      {
        id: "teams",
        title: "Team Management",
        badge: "Admin" as DocBadge,
        content: `# Team Management

Teams are the top-level organisational unit. Each team has its own chats, agents, files, meetings, and prompts.

## Team types

- **Home team** — automatically created for each user; private by default
- **Shared teams** — shared environments for a group or project

## Creating a team

Click **Team** in the sidebar → **Create team**. Give it a name and description. You'll be the team owner.

## Membership

Team members share access to:
- All agents assigned to the team
- Shared prompts
- Uploaded files
- Meeting recordings

Add members from the team settings. You can also assign agents to a team when creating or editing an agent.

## Team context

Each team can have a **context** — a text block the AI reads at the start of every conversation in that team. Use it to provide standing background: project names, client details, team conventions.

## Switching teams

Use the **Team** button in the sidebar, or the team switcher in the URL (\`?t=<team-id>\`). Chats are filtered to the active team.
`,
      },
      {
        id: "users",
        title: "User Management",
        badge: "Admin" as DocBadge,
        content: `# User Management

Admins can manage all users in the system from the Users panel.

## Accessing the Users panel

Click **More → Users** in the sidebar. You'll see a searchable list of all users with key stats.

## User roles

| Role | Access |
|------|--------|
| **User** | Standard access — chat, agents, meetings, files, prompts, memories |
| **Admin** | + user management, analytics, tool creation, editing built-in layouts, team ownership |
| **Superadmin** | + AI model management, system config, admin chats review |

Change a user's role by clicking their row in the Users panel and selecting a new role from the dropdown.

## User stats

The Users panel shows each user's:
- Number of files, agents, prompts, teams
- Last sign-in date
- Sign-up date
- Team memberships

## Onboarding status

New users go through an onboarding flow on first login. The onboarding flag is visible in the Users panel so admins can see who has completed setup.
`,
      },
      {
        id: "analytics",
        title: "Analytics",
        badge: "Admin" as DocBadge,
        content: `# Analytics

The Analytics panel gives admins a view of system usage across all users and teams.

## Accessing analytics

Click **More → Analytics** in the sidebar.

## Available metrics

### Users
- Total users
- New users in period
- Active users in period

### Chats & Messages
- Total chats
- Chats created in period
- Messages sent in period
- Total chat tokens and token trend by period
- Messages broken down by model
- User message distribution (by quartile)

### Content
- Agents created (total and in period), broken down by type (Knowledge / Custom)
- Prompts created
- Tools created
- Files uploaded and total storage used

### Meetings
- Recordings created in period

### Feedback
- Positive vs negative feedback counts
- Feedback sentiment trend

## Time range

Filter all metrics by: **7 days**, **14 days**, **30 days**, **90 days**, or **all time**.

## Charts

Activity trends are shown as bar and line charts. Model usage is shown as a pie chart. All charts update when you change the time range filter.
`,
      },
      {
        id: "large-file-monitoring",
        title: "Large File Monitoring",
        badge: "Admin" as DocBadge,
        content: `# Large File Monitoring

Large File Monitoring lets admins track Library files that are being processed through LandingAI's async parse job flow.

> **Admin access required.** This page is for operational monitoring and intervention when large document processing is delayed or fails.

## When files appear here

Files appear in Large File Monitoring when Woods AI sends them to LandingAI as async parse jobs. This usually happens for:

- Large PDFs over the sync parsing size threshold
- PDFs over the sync parsing page limit
- PDFs where the page count cannot be detected before upload, but LandingAI rejects sync parsing because the document is too large

Smaller files normally process through the standard Library upload flow and may not appear on this page.

## Accessing the page

Click **More -> Large File Monitoring** in the sidebar.

## What the page shows

The page gives admins a system-level view of async document processing:

| Area | What it means |
|---|---|
| **Parse Poller** | Shows whether the safety-net poller has run recently |
| **Active Jobs** | Files currently waiting for LandingAI to return parsed output |
| **Processing** | Files currently moving through the post-parse pipeline |
| **Completed** | Large files that have finished parsing and indexing |
| **Failed** | Files that failed, timed out, or were manually cancelled |
| **Status Distribution** | A chart showing the overall large-document status mix |

## Active parse jobs

The Active Parse Jobs table lists documents that are still waiting on async parse completion. Each row includes:

- File name
- LandingAI parse job ID, when available
- Time since submission
- Current queued status
- A cancel action

Use this table when a user reports that a large Library file has been uploading or processing for longer than expected.

## Cancelling a stuck job

Use **Cancel** only when a job is clearly stuck or no longer needed.

Cancelling marks the file as failed in Woods AI. It does not recover partial parsed content. The user will need to upload the file again if they still want it processed.

## Poller safety net

LandingAI normally sends parsed output back to Woods AI through the webhook. The parse poller is a fallback that checks for jobs stuck in the parse-job state beyond the configured timeout and marks them as failed so users are not left waiting indefinitely.

If the poller has not run recently, the most common cause is that the cron job was created without a valid URL. Fix it by running the following in the **Supabase SQL Editor** (replace \`<your-ref>\` with your project ref from the dashboard URL):

\`\`\`sql
SELECT cron.unschedule('woods-landingai-parse-job-poller');

SELECT cron.schedule(
  'woods-landingai-parse-job-poller',
  '*/5 * * * *',
  $cron$
  SELECT net.http_post(
    url     := 'https://<your-ref>.supabase.co/functions/v1/woods-landingai-parse-poller',
    headers := '{"Content-Type": "application/json"}'::jsonb,
    body    := '{}'::jsonb
  );
  $cron$
);
\`\`\`

After running this, wait one minute and refresh — the Parse Poller card should show a recent successful run.

## Admin workflow

1. Open **More -> Large File Monitoring**
2. Check the **Parse Poller** card first
3. Review counts for active, processing, completed, and failed files
4. Inspect **Active Parse Jobs** for old queued jobs
5. Cancel only jobs that are clearly stuck or no longer required
6. Ask the user to re-upload the file if processing was cancelled or failed

## Related Library behaviour

Users upload large files from the normal **Library** page. They do not need to choose a large-file mode manually. Woods AI decides whether to use sync parsing or async parsing based on file size, PDF page count, and LandingAI's response.
`,
      },
      {
        id: "ai-models",
        title: "AI Models",
        badge: "Admin" as DocBadge,
        content: `# AI Models

Superadmins can manage which AI models are available to users.

## Accessing model management

Click **More → AI Models** in the sidebar (Superadmin only).

## Model catalogue

The model catalogue is synced from the Anthropic and OpenAI APIs. Each model entry shows:

- **Name and ID**
- **Provider** (Anthropic, OpenAI)
- **Tier** — Standard, Advanced, Expert, or Experimental
- **Capabilities** — writing, research, coding, reasoning, idea generation, problem solving, image, audio
- **Context length**
- **Cost per million tokens** (input and output)
- **Reasoning effort** — for models that support it (low / medium / high)

## Activating and deactivating models

Toggle the **Active** switch on any model to control whether it appears in the model selector for users. Deactivated models are hidden but not removed from the catalogue.

## Adding models

Click **Sync catalogue** to pull the latest models from the provider APIs. New models appear as inactive by default — review and activate them manually.
`,
      },
      {
        id: "managing-tools",
        title: "Managing Tools",
        badge: "Admin" as DocBadge,
        content: `# Managing Tools

This guide explains how the tool system works and how to add new tools — whether they're built-in AI provider tools (Anthropic, OpenAI) or custom integrations.

> **Admin access required** to create, edit, or deactivate tools.

---

## How the tool system works

Every tool is a row in the **tools** table. The route handler reads the tools the user or agent has enabled, looks up the row, and dispatches based on \`executor_config.provider\` and \`executor_config.feature_key\`.

This means adding a new tool is **entirely database-driven** — no code changes required for standard provider tools.

### Key fields

| Field | Purpose |
|---|---|
| \`source_type\` | \`builtin\` — managed by the system. \`custom\` — created by a user or admin. |
| \`executor_type\` | \`provider\` (AI built-in), \`edge_function\`, \`webhook\`, \`aws_ecs\`, \`mcp\` |
| \`scope\` | \`global\` (all users), \`team\` (team members), \`user\` (creator only) |
| \`executor_config\` | JSON blob that tells the route how to instantiate the tool (see below) |
| \`config_schema\` | JSON Schema for per-instance config (e.g. knowledge base file IDs) |
| \`enabled\` | Soft disable — hides the tool without deleting it |

### executor_config shapes

**Single-provider tool (e.g. webFetch — Anthropic only):**
\`\`\`json
{
  "provider":    "anthropic",
  "feature_key": "web_fetch_20260209",
  "tool_name":   "web_fetch",
  "tool_id":     "webFetch",
  "options":     { "max_uses": 10 }
}
\`\`\`

**Multi-provider tool (e.g. Web Search — Anthropic + OpenAI):**
\`\`\`json
{
  "tool_id":   "webSearch",
  "tool_name": "web_search",
  "providers": {
    "anthropic": {
      "feature_key": "web_search_20260209",
      "options":     { "max_uses": 10 }
    },
    "openai": {
      "feature_key": "web_search_preview",
      "tool_type":   "web_search_preview",
      "tool_name":   "web_search_openai",
      "options":     { "search_context_size": "medium" }
    }
  }
}
\`\`\`

When \`providers\` is present the route selects the sub-config that matches the active model's provider (\`ModelConfig.provider\`). If the model's provider is not listed, the tool is silently skipped. The sub-config is spread over the base, so any key it sets — including \`tool_name\` — overrides the top-level value.

**Every provider in a \`providers\` map must have a distinct \`tool_name\`.** The \`tool_name\` is the key sent to the model API and the name persisted on every UI message part. Each provider's prompt converter resolves history parts by NAME, so a shared name makes one provider validate the other's stored output against its own schema and throw \`AI_TypeValidationError\` — bricking the thread on every load. Anthropic and OpenAI web search shared \`web_search\` until migration \`20260824000001\`.

**Internal edge function:**
\`\`\`json
{
  "provider":    "internal",
  "feature_key": "knowledge_base",
  "tool_name":   "knowledge_base",
  "tool_id":     "knowledgeBase"
}
\`\`\`

**Custom webhook:**
\`\`\`json
{
  "provider":    "webhook",
  "url":         "https://api.yourcompany.com/tools/lookup",
  "method":      "POST",
  "headers":     { "Authorization": "Bearer {{SECRET}}" }
}
\`\`\`

---

## Builtin tool IDs (stable UUIDs)

These UUIDs are seeded in migrations and should never change.
\`a2\` and \`a4\` were removed in migration \`20260430000004\` — their functionality is now inside \`a1\` and \`a3\` via the \`providers\` map.

| Tool | UUID |
|---|---|
| Web Search (Anthropic + OpenAI) | \`a1000000-0000-0000-0000-000000000001\` |
| Compute Engine (Anthropic + OpenAI) | \`a3000000-0000-0000-0000-000000000003\` |
| Memory (Anthropic) | \`a6000000-0000-0000-0000-000000000006\` |
| Knowledge Base (Internal) | \`a7000000-0000-0000-0000-000000000007\` |
| Web Fetch (Anthropic) | \`a8000000-0000-0000-0000-000000000008\` |
| Writing Style Profile (Internal) | \`a9000000-0000-0000-0000-000000000009\` |

---

## Adding a new Anthropic built-in tool

When Anthropic releases a new tool (e.g. a future \`imageGen_20260101\`):

1. Find the SDK method name from the \`@ai-sdk/anthropic\` changelog.

2. Add a migration that inserts a row. Use a single-provider config if Anthropic only, or a \`providers\` map if the tool has an OpenAI equivalent:

\`\`\`sql
INSERT INTO tools (
    id, created_by, source_type, executor_type, scope,
    name, description, icon, enabled, schema, executor_config, created_at
) VALUES (
    'a9000000-0000-0000-0000-000000000009',   -- new stable UUID
    NULL, 'builtin', 'provider', 'global',
    'Image Generation',
    'Generate images from a text description.',
    'image', true, '{}',
    '{
        "provider":    "anthropic",
        "feature_key": "imageGen_20260101",
        "tool_name":   "image_generation",
        "tool_id":     "imageGeneration"
    }'::jsonb,
    CURRENT_TIMESTAMP
) ON CONFLICT (id) DO NOTHING;
\`\`\`

3. Add the UUID to \`BUILTIN_TOOL_UUID\` in \`src/lib/builtin-tools.ts\`:

\`\`\`ts
export const BUILTIN_TOOL_UUID: Record<ToolId, string> = {
  // ...existing
  imageGeneration: "a9000000-0000-0000-0000-000000000009",
};
\`\`\`

4. Add \`"imageGeneration"\` to the \`ToolId\` union in \`src/types/settings.ts\`.

5. Add a \`case "imageGen_20260101":\` branch to \`buildToolsFromActive()\` in the chat route — this is the only code change needed.

6. If the tool also has an OpenAI equivalent, use the \`providers\` map in \`executor_config\` instead of a flat \`provider\`/\`feature_key\`. No code change is needed for the OpenAI side — the route dispatches it automatically once that model provider is added to \`MODEL_CONFIG\`.

7. Decide on default: add it to the \`set_default_profile_tools\` trigger backfill if it should be on by default for all users (like web search), or leave it off (like knowledge base).

---

## Adding an OpenAI-equivalent to an existing tool

To extend an existing single-provider builtin to also support OpenAI:

1. Write a migration that changes \`executor_config\` to the \`providers\` map shape (see example in \`20260430000004_merge_openai_builtin_tools.sql\`).
2. Add the OpenAI model to \`MODEL_CONFIG\` in the chat route with \`provider: "openai"\`.
3. Handle the \`feature_key\` in the \`provider === "openai"\` branch of \`buildToolsFromActive()\`.

No new DB rows or \`BUILTIN_TOOL_UUID\` entries are needed.

---

## Adding a custom webhook tool (no code changes)

Custom tools that call an external HTTP endpoint require no code changes:

1. Go to **More → Tools** in the sidebar
2. Click **Create new tool**
3. Fill in:
   - **Name** — shown to users and read by the AI to decide when to use the tool
   - **Description** — be specific: "Look up a project record by project number in the company database"
   - **Executor type** — Webhook
   - **URL** — the endpoint the route will POST to
   - **Authentication** — API key or Bearer token (stored in \`executor_config.headers\`)
   - **Parameters (schema)** — JSON Schema describing what inputs the AI should provide

4. Assign to a team

**Example schema for a project lookup tool:**
\`\`\`json
{
  "type": "object",
  "properties": {
    "project_number": {
      "type": "string",
      "description": "The company project number, e.g. P-1234"
    }
  },
  "required": ["project_number"]
}
\`\`\`

The route will POST this JSON to your endpoint and return the response to the AI.

---

## Controlling which tools are on by default

**For all users (profile defaults):**

The \`set_default_profile_tools\` trigger in \`20260429000006\` populates \`profiles.profile_tools\` for new users. To include a new tool in everyone's defaults:

\`\`\`sql
-- Re-run backfill after adding the new tool
UPDATE profiles
SET profile_tools = (
  SELECT jsonb_agg(jsonb_build_object('tool_id', t.id::text, 'config', '{}'))
  FROM tools t
  WHERE t.source_type = 'builtin'
    AND t.executor_config->>'tool_id' != 'knowledgeBase'
    -- Add other exclusions here if needed
)
WHERE profile_tools IS NOT NULL;
\`\`\`

**Knowledge Base is always off by default** — it is only meaningful when an agent explicitly enables it with specific file IDs.

**For an agent:**

In the agent editor, go to **Tools** and toggle the tool on. The agent's tool list overrides the user's default for any conversation using that agent.

---

## Deactivating a tool

Set \`enabled = false\` on the tools row (or use the toggle in the admin UI). Disabled tools are hidden from the tool picker and excluded from all agent and user tool lists. No rows are deleted — the tool can be re-enabled at any time.
`,
      },
      {
        id: "meeting-notes-pipeline",
        title: "Meeting Notes Pipeline",
        badge: "Admin" as DocBadge,
        content: `# Meeting Notes Pipeline

This page describes the end-to-end background pipeline that runs after a user stops a meeting recording and presses **Create Notes**.

> **Admin / Superadmin reference.** Users do not need to understand this flow — it runs entirely in the background.

---

## Overview

The pipeline is split into three asynchronous edge functions that each fire-and-forget into the next. This keeps response times low and lets each stage fail and retry independently.

\`\`\`mermaid
flowchart TD
    A([User stops recording]) --> A1
    A1[Set status = RECORDING_COMPLETE\nin DB] --> B

    subgraph Client ["Browser"]
        B[Set status = PROCESSING_NOTES\nin DB]
        B --> C[Fire-and-forget\ngenerate-meeting-minutes]
        C --> D[Redirect to home\nUI polls every 8 s]
    end

    subgraph Stage1 ["Edge Function — generate-meeting-minutes"]
        E[Load transcript\nmeeting context\nprevious notes from DB]
        E --> F[Call Anthropic with\nextended thinking\nand QA retry loop]
        F --> G{Minutes pass\nvalidation?}
        G -- Yes --> H[Save minutes + title\nSet status = PROCESSING_DOCX]
        G -- No, retry --> F
        H --> I[Fire-and-forget\ngenerate-meeting-notes-docx]
    end

    subgraph Stage2 ["Edge Function — generate-meeting-notes-docx"]
        J[Load minutes + title\nfrom DB]
        J --> K[Multi-step Anthropic\ncontainer loop\nwoods-meeting-notes-docx skill]
        K --> L[Download DOCX\nfrom Anthropic Files API]
        L --> M[Upload DOCX\nto Supabase Storage]
        M --> N[Persist notes_path\nSet status = SENDING_EMAIL]
        N --> O[Fire-and-forget\nsend-meeting-minutes-email]
    end

    subgraph Stage3 ["Edge Function — send-meeting-minutes-email"]
        P[Load recording + attendees\nfrom DB]
        P --> Q[Download DOCX\nfrom Storage]
        Q --> R[Build branded HTML email\nusing _shared/email.ts]
        R --> S[Send via SMTP\nDOCX attached]
        S --> T[Set status = PROCESSING_COMPLETE\nPersist email_sent = true]
    end

    C -.->|async| E
    I -.->|async| J
    O -.->|async| P

    D -->|polls every 8 s| H
    H -->|status = PROCESSING_DOCX| D
    N -->|status = SENDING_EMAIL| D
    T -->|status = PROCESSING_COMPLETE\nnotes_path arrives| D
\`\`\`

---

## Stage 1 — generate-meeting-minutes

**Trigger:** called by the browser (fire-and-forget) when the user confirms note generation.

**What it does:**

1. Loads \`transcript\`, \`meeting_context\`, \`meeting_instructions\`, \`previous_meeting_notes\`, and the selected \`layout\` from \`meeting_recordings\`.
2. Builds an Anthropic prompt with the layout structure as the system prompt and the transcript + context as user content. \`meeting_instructions\` are passed as author directions for *how* to write the minutes; they can shape tone, depth and emphasis but never override the layout's required headings, the JSON output contract, or the transcript-as-sole-source rule.
3. Runs a **validation retry loop** (up to 2 attempts). Each attempt checks the generated text for structural issues. Failures are logged with \`console.warn\` including the issue list.
4. Extended thinking budget scales with transcript length:
   - < 300 words → no thinking budget
   - 300–1500 words → 4 000 tokens
   - > 1500 words → 12 000 tokens
5. On success, saves \`minutes\`, updates \`title\`, sets \`status = PROCESSING_DOCX\`.
6. Fires \`generate-meeting-notes-docx\` asynchronously.

**Model:** controlled by the \`ANTHROPIC_MEETING_NOTES_MODEL\` Supabase secret (defaults to \`claude-opus-4-8\`).

---

## Stage 2 — generate-meeting-notes-docx

**Trigger:** fire-and-forget from Stage 1.

**What it does:**

1. Loads \`minutes\` and \`title\` from DB.
2. Opens a multi-step Anthropic **container session** using the \`woods-docx\` custom skill:
   - Dev skill ID: \`skill_01Lyh93SjLGHaZi8Ku3qBKJ7\`
   - Prod skill ID: \`skill_013P6BLG9f3knhTSws3vUNsd\`
3. Forwards the container ID across turns (up to 5 steps) so the skill can write a file.
4. Extracts the generated \`file_id\` from \`code_execution_result\` content blocks.
5. Downloads the DOCX from the **Anthropic Files API**.
6. Uploads to Supabase Storage: \`files/{userId}/meeting-minutes/{recordingId}/minutes.docx\`
7. Persists the storage path in \`meeting_recordings.notes_path\`.
8. Fires \`send-meeting-minutes-email\` asynchronously.

**Environment switch:** \`VERCEL_API_ENVIRONMENT=production\` selects the prod skill ID; anything else uses dev.

---

## Stage 3 — send-meeting-minutes-email

**Trigger:** fire-and-forget from Stage 2.

**What it does:**

1. Loads the recording, minutes, and attendees from DB.
2. Downloads the DOCX from Supabase Storage using \`notes_path\`.
3. Renders a branded HTML email body using \`supabase/functions/_shared/email.ts\`.
4. Sends via SMTP (denomailer) with the DOCX base64-encoded as an attachment.
5. If the DOCX download fails, logs a warning and sends the email **without** the attachment rather than failing the whole send.
6. Sets \`email_sent = true\` and \`email_sent_at\` on the recording row.

---

## DB columns involved

| Column | Table | Set by |
|---|---|---|
| \`status\` | \`meeting_recordings\` | Browser (→ RECORDING / RECORDING_COMPLETE / PROCESSING_NOTES), Stage 1 (→ PROCESSING_DOCX / FAILED), Stage 2 (→ SENDING_EMAIL / FAILED), Stage 3 (→ PROCESSING_COMPLETE) |
| \`minutes\` | \`meeting_recordings\` | Stage 1 |
| \`title\` | \`meeting_recordings\` | Stage 1 (auto-generated) |
| \`notes_path\` | \`meeting_recordings\` | Stage 2 |
| \`email_sent\` | \`meeting_recordings\` | Stage 3 |
| \`email_sent_at\` | \`meeting_recordings\` | Stage 3 |

---

## Client polling

While \`status\` is \`PROCESSING_NOTES\`, \`PROCESSING_DOCX\`, or \`SENDING_EMAIL\`, the meeting page polls the DB every **8 seconds** for \`status\`, \`minutes\`, \`title\`, \`email_sent\`, \`email_sent_at\`, and \`notes_path\`.

**Transitions the client reacts to:**

- \`PROCESSING_NOTES → PROCESSING_DOCX\`: Minutes are ready. Draft is populated, title-refinement API call fires, toast shown. Download button shows a DOCX-generating spinner.
- \`PROCESSING_DOCX → SENDING_EMAIL\`: DOCX done, email sending.
- \`SENDING_EMAIL → PROCESSING_COMPLETE\`: \`notes_path\` arrives, download button activates.
- Any → \`FAILED\`: Retry button appears. Retry is smart — if \`minutes\` already exist, it retries from Stage 2 (DOCX) only; otherwise it retries from Stage 1.

---

## Failure modes

| Symptom | Likely cause | Action |
|---|---|---|
| Minutes never arrive, status stuck at \`PROCESSING_NOTES\` | Stage 1 timed out or Anthropic error | Check edge function logs for \`generate-meeting-minutes\`. Use the **Retry** button on the meeting page. |
| Minutes visible but no DOCX download | Stage 2 failed | Check logs for \`generate-meeting-notes-docx\`. Skill ID mismatch between env. |
| Email not received | Stage 3 failed or SMTP misconfigured | Check \`email_sent\` column; check SMTP secrets in Supabase vault. |
| Email received without DOCX attachment | Stage 2 completed but \`notes_path\` was missing when Stage 3 ran | Stage 2 logs will show storage/upload error. |

---

## Required secrets (Supabase vault)

| Secret | Used by |
|---|---|
| \`ANTHROPIC_API_KEY\` | Stages 1 and 2 |
| \`ANTHROPIC_MEETING_NOTES_MODEL\` | Stage 1 (minutes generation, defaults to \`claude-opus-4-8\`) |
| \`ANTHROPIC_MEETING_NOTES_DOCX_MODEL\` | Stage 2 (DOCX generation, defaults to \`claude-sonnet-5\`) |
| \`VERCEL_API_ENVIRONMENT\` | Stage 2 (skill ID selection) |
| \`SMTP_SERVER\`, \`SMTP_PORT\`, \`SMTP_USERNAME\`, \`SMTP_PASSWORD\`, \`SMTP_SENDER\` | Stage 3 |

---

## Transcript retention

A transcript is the raw, verbatim record of a meeting. It is kept only as long as it is
needed to (re-)draft the minutes, then removed. Generated minutes, the DOCX and the KB
index are **not** affected — only the transcript text.

| Day | What happens |
|---|---|
| 0 | Meeting recorded. Transcript stored on the row. |
| Any | Stage 3 sends the minutes email → transcript nulled immediately. |
| 25 | \`woods-meeting-transcript-reminder\` emails the owner a digest of every meeting of theirs still missing a DOCX or a sent email, grouped personal / per-project, each linked. |
| 30 | \`woods-meeting-transcript-purge\` nulls the transcript on both \`meeting_recordings\` and \`project_meeting_recordings\`. |

**Cron jobs** (both daily, defined in migration \`20260921000001_meeting_transcript_retention.sql\`):

- \`woods-meeting-transcript-reminder\` — 20:00 UTC (08:00 NZST) → \`dispatch_meeting_transcript_reminders()\` → \`send-meeting-transcript-reminder\` edge function.
- \`woods-meeting-transcript-purge\` — 14:40 UTC (02:40 NZST) → \`purge_expired_meeting_transcripts()\`. Pure SQL, no edge function.

**Never touched:** meetings with status \`RECORDING\`, \`PROCESSING_NOTES\`, \`PROCESSING_DOCX\` or
\`SENDING_EMAIL\` — the transcript is the input to whatever is running.

**Supporting objects:**

| Object | Role |
|---|---|
| \`meeting_transcripts_pending_action\` | View over both meeting tables: rows that still hold a transcript and have not been through DOCX + email. \`security_invoker\`, so normal RLS applies. |
| \`transcript_warning_sent_at\` | Column on both tables. One warning per meeting; survives a move between personal and project scope. |
| \`meeting_transcript_purges\` | Audit log of every removal (id, table, owner, size, whether the owner was warned). Holds no transcript content. |

**Required secrets:** \`MEETING_TRANSCRIPT_CRON_SECRET\` (edge function) must match the
\`app.meeting_transcript_cron_secret\` database setting. When they do not, the reminder POST
is rejected with a 401 — which is what stops non-production environments emailing real
users. The purge is not gated and runs in every environment.
`,
      },
    ],
  },
];

export function getAllDocs(): DocItem[] {
  return DOC_SECTIONS.flatMap((section) => section.items);
}

export function findDoc(id: string): DocItem | undefined {
  return getAllDocs().find((doc) => doc.id === id);
}

export function searchDocs(query: string): { section: DocSection; item: DocItem }[] {
  const q = query.toLowerCase();
  const results: { section: DocSection; item: DocItem }[] = [];

  for (const section of DOC_SECTIONS) {
    for (const item of section.items) {
      if (
        item.title.toLowerCase().includes(q) ||
        item.content.toLowerCase().includes(q)
      ) {
        results.push({ section, item });
      }
    }
  }

  return results;
}
